import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { useSyncLanguage } from "@/hooks/useLanguage";
import JsonLd, { websiteJsonLd } from "@/components/feature/JsonLd";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import CategoryDropdown from "@/components/feature/CategoryDropdown";
import CategoryGrid from "@/components/feature/CategoryGrid";
import { HERO_IMAGE, MAIN_SITE_URL } from "@/mocks/site";
import { PILLAR_ARTICLES } from "@/mocks/articles";

const STEP_ICONS: Record<number, string> = {
  1: "ri-price-tag-3-line",
  2: "ri-search-eye-line",
  3: "ri-shield-check-line",
  4: "ri-external-link-line",
};

export default function Home() {
  const { lang: langParam } = useParams();
  const lang = useSyncLanguage(langParam);
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={websiteJsonLd(lang)} />
      <SiteHeader lang={lang} />

      <main>
        {/* Hero */}
        <section className="relative">
          <div className="absolute inset-0">
            <img
              src={HERO_IMAGE}
              alt="USFans Spreadsheet — product discovery resource"
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40" />
          <div className="relative mx-auto flex w-full max-w-7xl flex-col items-center px-4 py-20 text-center md:px-6 md:py-28">
            <span className="inline-flex items-center gap-2 rounded-full bg-background-50/90 px-4 py-1.5 text-xs font-medium text-foreground-800">
              <i className="ri-shield-check-line text-primary-600" />
              {t("hero.badge")}
            </span>
            <h1 className="mt-6 max-w-3xl font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
              {t("hero.title")}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-background-100/90 md:text-lg">
              {t("hero.subtitle")}
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("cta.spreadsheet")}
              </a>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                {t("cta.buyNow")}
              </a>
            </div>

            <p className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs font-medium text-background-100/80">
              <span className="flex items-center gap-1.5">
                <i className="ri-checkbox-circle-line text-primary-300" /> {t("hero.trust")}
              </span>
            </p>
          </div>
        </section>

        {/* Intro */}
        <section className="mx-auto max-w-4xl px-4 py-14 md:px-6 md:py-20">
          <h2 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">{t("intro.title")}</h2>
          <div className="mt-5 space-y-4 text-base leading-relaxed text-foreground-700">
            <p>{t("intro.p1")}</p>
            <p>{t("intro.p2")}</p>
          </div>
        </section>

        {/* Category dropdown + CTA */}
        <section className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-8">
            <CategoryDropdown />
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("cta.explore")}
              </a>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                {t("cta.browse")}
              </a>
            </div>
          </div>
        </section>

        {/* Category grid */}
        <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <h2 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">{t("categories.title")}</h2>
          <p className="mt-3 max-w-2xl text-base text-foreground-600">{t("categories.subtitle")}</p>
          <div className="mt-8">
            <CategoryGrid />
          </div>
        </section>

        {/* How it works */}
        <section className="border-y border-background-200 bg-background-100/50">
          <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
            <h2 className="text-center font-heading text-2xl font-bold text-foreground-900 md:text-3xl">
              {t("how.title")}
            </h2>
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                    <i className={STEP_ICONS[step]} />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-900">
                    {t(`how.step${step}.title`)}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t(`how.step${step}.text`)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Guide article entry */}
        <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="flex flex-col gap-6 rounded-lg border border-background-200 bg-background-100/60 p-6 md:flex-row md:items-center md:justify-between md:p-10">
            <div className="max-w-2xl">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">{t("guide.title")}</h2>
              <p className="mt-3 text-base leading-relaxed text-foreground-600">{t("guide.subtitle")}</p>
            </div>
            <Link
              to={`/${lang}/article/usfans-spreadsheet-guide`}
              className="whitespace-nowrap rounded-md bg-secondary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-600"
            >
              {t("guide.read")} <i className="ri-arrow-right-line ml-1" />
            </Link>
          </div>
        </section>

        {/* Latest articles */}
        <section className="border-y border-background-200 bg-background-100/50">
          <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
            <h2 className="text-center font-heading text-2xl font-bold text-foreground-900 md:text-3xl">
              {t("articles.title")}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-base text-foreground-600">
              {t("articles.subtitle")}
            </p>
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {PILLAR_ARTICLES.map((article) => (
                <Link
                  key={article.slug}
                  to={`/${lang}/article/${article.slug}`}
                  className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
                >
                  <div className="h-44 w-full overflow-hidden">
                    <img
                      src={article.image}
                      alt={`${article.title[lang] || article.title.en} — USFans Spreadsheet`}
                      title={`${article.title[lang] || article.title.en} — USFans Spreadsheet`}
                      className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                      width={600}
                      height={338}
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-heading text-base font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
                      {article.title[lang] || article.title.en}
                    </h3>
                    <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">
                      {article.summary[lang] || article.summary.en}
                    </p>
                    <span className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-primary-700">
                      {t("articles.readMore")}
                      <i className="ri-arrow-right-line text-sm transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-4xl px-4 py-14 md:px-6 md:py-20">
          <h2 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">{t("faq.title")}</h2>
          <div className="mt-6 space-y-4">
            {[1, 2, 3, 4].map((q) => (
              <details key={q} className="group rounded-lg border border-background-200 bg-background-50 p-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-heading text-base font-semibold text-foreground-900">
                  {t(`faq.q${q}`)}
                  <i className="ri-add-line text-lg text-foreground-500 transition-transform group-open:rotate-45" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{t(`faq.a${q}`)}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="mx-auto max-w-7xl px-4 pb-4 md:px-6">
          <div className="rounded-lg bg-primary-500 px-6 py-12 text-center md:py-16">
            <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">{t("cta.title")}</h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-background-100/90">{t("cta.subtitle")}</p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
              >
                {t("cta.spreadsheet")}
              </a>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                {t("cta.buyNow")}
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter lang={lang} />
    </div>
  );
}